//@flow
import {CircularProgress, makeStyles, Typography} from '@material-ui/core';
import {Publish} from '@material-ui/icons';
import React, {useState} from 'react';
import {colors} from '../../assets/styles/colors';
import CustomButton from '../CustomButton/CustomButton';
import CustomModal from '../CustomModal/CustomModal';
import ImageItem from './ImageItem';
import CropModal from '../CropModal';

import {useSafePrevious} from '../../utils/react';

import type {ImageToCrop} from '../CropModal';
import {useProductImageShape} from '../../modules/ui/utils';
type ImageFile = {
  preview: string,
  id: string,
};

type ImageSelectedResult = {
  id?: string,
  isNew?: boolean,
  file?: File,
};

type SubmitResult = {
  imageSelected?: ImageSelectedResult,
  newImages?: File[],
  editedImages?: ImageFile[],
};

type Props = {
  open: boolean,
  onClose: () => void,
  onSave: (result: SubmitResult) => void,
  title?: string,
  images: ImageFile[],
  defaultSelected?: string,
  selectedLabel?: string,
  canDelete?: boolean,
  canCrop?: boolean,
  canUpload?: boolean,
  canSelect?: boolean,
  loading?: boolean,
  selectOnUpload?: boolean,
};

const useStyles = makeStyles(theme => ({
  container: {
    padding: '0 1rem',
    width: '90vh',
    maxWidth: '500px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
    maxHeight: '500px',
    overflow: 'hidden',
    gap: '1rem',
  },
  loader: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(244,244,246,.8)',
    zIndex: 10,
    top: 0,
    left: 0,
    cursor: 'progress',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  uploadImage: {
    width: 132,
    height: 132,
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '.95rem',
    border: `3px dashed ${colors.grey500}`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    flexDirection: 'column',
    gap: '.25rem',
  },
  imagesGrid: {
    flexGrow: 1,
    width: '100%',
    overflow: 'auto',
    display: 'grid',
    height: '100%',
    gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))',
    gap: '1rem',
  },
  submitButton: {
    maxWidth: '240px',
    fontSize: '1.125rem',
    fontWeight: 600,
  },
}));

const SIZE_RECOMMENDATION = {
  SQUARE: {
    width: 1080,
    height: 1080,
  },
  RECTANGLE: {
    width: 1080,
    height: 1440,
  },
};
const MAX_SIZE_COMPRESS = {
  SQUARE: {
    width: 2160,
    height: 2160,
  },
  RECTANGLE: {
    width: 2160,
    height: 3840,
  },
};

export default function ProductImagesModal(props: Props) {
  const {
    onClose,
    onSave,
    images,
    title = 'Imagenes del producto',
    open,
    defaultSelected,
    selectedLabel,
    canDelete = false,
    // canCrop = false,
    canUpload = false,
    canSelect = false,
    loading = false,
    selectOnUpload = false,
  } = props;

  const classes = useStyles();

  const productImageShape = useProductImageShape();

  const [imagesList, setImagesList] = useState<ImageFile[]>(images);

  const [newImages, setNewImages] = useState<ImageToCrop[]>([]);

  const [selected, setSelected] = useState(defaultSelected);

  const [files, setFiles] = useState<File[] | null>(null);

  const previousPoster = useSafePrevious(selected);

  const handleUploadImage = (e: SyntheticInputEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files.length > 0) {
      setFiles(Array.from(files));
    }
  };

  const handleSave = () => {
    const submitResult: SubmitResult = {};

    if (defaultSelected !== selected) {
      const posterFromNewImages = newImages.find(
        image => image.preview === selected
      );

      if (posterFromNewImages) {
        submitResult.imageSelected = {
          isNew: true,
          file: posterFromNewImages.file,
        };
      } else {
        const posterFromSavedImages = imagesList.find(
          image => image.preview === selected
        );

        if (posterFromSavedImages) {
          submitResult.imageSelected = {
            id: posterFromSavedImages.id,
          };
        }
      }
    }

    if (newImages.length > 0) {
      submitResult.newImages = newImages.map(image => image.file);
    }

    if (imagesList.length !== images.length) {
      submitResult.editedImages = imagesList;
    }

    onSave(submitResult);
  };

  return (
    <>
      <CustomModal open={open} handleCloseModal={onClose}>
        <aside className={classes.container}>
          {loading && (
            <div className={classes.loader}>
              <CircularProgress color="primary" />
            </div>
          )}
          <Typography variant="h6" className={classes.title}>
            {title}
          </Typography>

          <div className={classes.imagesGrid}>
            {canUpload && (
              <label className={classes.uploadImage} role="button">
                <Publish color="primary" />
                <input
                  type="file"
                  name="productPhoto"
                  hidden
                  onChange={handleUploadImage}
                  accept="image/*"
                />

                <span>{`${SIZE_RECOMMENDATION[productImageShape].width} x ${SIZE_RECOMMENDATION[productImageShape].height}`}</span>
              </label>
            )}
            {imagesList.map(({preview, id}, index) => {
              const isSelected = preview === selected;

              // const onCrop = imageId => {};

              const onClick = () => {
                setSelected(preview);
              };

              const onRemove = () => {
                if (isSelected) {
                  setSelected(previousPoster);
                }

                setImagesList(prevList =>
                  prevList ? prevList.filter(image => image.id !== id) : []
                );
              };

              return (
                <ImageItem
                  key={index}
                  src={preview}
                  isSelected={isSelected}
                  selectedLabel={selectedLabel}
                  // onCrop={onCrop}
                  onClick={canSelect ? onClick : undefined}
                  onRemove={canDelete ? onRemove : undefined}
                />
              );
            })}
            {newImages.map(({preview, file}, index) => {
              const isSelected = preview === selected;

              // const onCrop = imageId => {};

              const onClick = () => {
                setSelected(preview);
              };

              const onRemove = () => {
                if (isSelected) {
                  setSelected(previousPoster);
                }

                setNewImages(prevList =>
                  prevList.filter((_, i) => i !== index)
                );
              };

              return (
                <ImageItem
                  key={index}
                  src={preview}
                  isSelected={isSelected}
                  // onCrop={onCrop}
                  selectedLabel={selectedLabel}
                  onClick={canSelect ? onClick : undefined}
                  onRemove={onRemove}
                />
              );
            })}
          </div>

          <CustomButton
            className={classes.submitButton}
            color="primary"
            variant="contained"
            fullWidth
            onClick={handleSave}
          >
            Guardar
          </CustomButton>
        </aside>
      </CustomModal>
      {files && (
        <CropModal
          open={!!files}
          aspect={productImageShape === 'RECTANGLE' ? 0.75 : 1}
          onClose={() => setFiles(null)}
          compressOptions={{
            maxWidth: MAX_SIZE_COMPRESS[productImageShape].width,
            maxHeight: MAX_SIZE_COMPRESS[productImageShape].height,
            quality: 0.8,
          }}
          onSubmit={results => {
            setNewImages(prevList => [...prevList, ...results]);
            setFiles(null);
            selectOnUpload && setSelected(results[0].preview);
          }}
          images={files.map(f => ({
            preview: URL.createObjectURL(f),
            file: f,
          }))}
        />
      )}
    </>
  );
}
